import React from 'react';

/**
 * ComplianceTab - Wired to Phase 1 eliminations + tender BI metadata
 */
const ComplianceTab = ({ tender, phase1Data }) => {
  const eliminations = phase1Data?.eliminations || [];
  const warnings = phase1Data?.warnings || [];
  const canBid = phase1Data?.can_bid;
  const hasBlocking = phase1Data?.has_blocking_eliminations;
  const bi = tender?.business_intelligence || {};

  const hardStops = eliminations.filter(e => e.severity === 'hard_stop');
  const userDecisions = eliminations.filter(e => e.severity === 'user_decision');

  const riskLevel = hardStops.length > 0 ? 'high' : userDecisions.length > 0 ? 'medium' : 'low';
  const riskConfig = {
    high: { label: 'High Risk', className: 'red', icon: '!!', desc: `${hardStops.length} blocking elimination${hardStops.length !== 1 ? 's' : ''} found` },
    medium: { label: 'Medium Risk', className: 'amber', icon: '!', desc: `${userDecisions.length} item${userDecisions.length !== 1 ? 's' : ''} requiring review` },
    low: { label: 'Low Risk', className: 'green', icon: '\u2713', desc: 'No eliminations identified' },
  };
  const risk = riskConfig[riskLevel];

  if (!phase1Data) {
    return (
      <div className="compliance-tab">
        <div className="no-assessment-card">
          <div className="no-assessment-icon">\u2713</div>
          <h4>No Compliance Data Yet</h4>
          <p>Run the analysis to generate compliance insights from Phase 1 qualification data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="compliance-tab">
      {/* 2b. Risk Summary */}
      <div className="summary-section">
        <h4 className="summary-section-title">Risk Summary</h4>
        <div className="decision-grid" style={{ marginBottom: 16 }}>
          <div className={`decision-card ${risk.className}`}>
            <div className="decision-header">
              <span className="decision-type">Overall Risk</span>
              <span className={`decision-badge ${riskLevel === 'low' ? 'proceed' : riskLevel === 'high' ? 'eliminate' : 'review'}`}>
                {risk.label}
              </span>
            </div>
            <p className="decision-reason">{risk.desc}</p>
          </div>
          <div className={`decision-card ${canBid ? 'green' : 'red'}`}>
            <div className="decision-header">
              <span className="decision-type">Can Bid</span>
              <span className={`decision-badge ${canBid ? 'proceed' : 'eliminate'}`}>
                {canBid ? 'Yes' : 'No'}
              </span>
            </div>
            <p className="decision-reason">
              {canBid
                ? 'Tender type allows bidding'
                : 'Tender type does not allow bidding'}
            </p>
          </div>
          <div className={`decision-card ${hasBlocking ? 'red' : 'green'}`}>
            <div className="decision-header">
              <span className="decision-type">Blocking Issues</span>
              <span className={`decision-badge ${hasBlocking ? 'eliminate' : 'proceed'}`}>
                {hasBlocking ? 'Yes' : 'None'}
              </span>
            </div>
            <p className="decision-reason">
              {hasBlocking
                ? `${hardStops.length} hard-stop elimination${hardStops.length !== 1 ? 's' : ''}`
                : 'No blocking eliminations'}
            </p>
          </div>
        </div>
      </div>

      {/* 2a. Compliance Matrix */}
      {eliminations.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title">Compliance Matrix</h4>
          <table className="products-table">
            <thead>
              <tr>
                <th>Requirement</th>
                <th>Type</th>
                <th>Severity</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {eliminations.map((e, i) => (
                <tr key={i} style={e.severity === 'hard_stop' ? { background: '#fef2f2' } : e.severity === 'user_decision' ? { background: '#fffbeb' } : {}}>
                  <td style={{ fontWeight: 600 }}>{e.description}</td>
                  <td>
                    <span className="heuristic-chip" style={{ fontSize: 11 }}>
                      {e.type?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td>
                    <span className={`qual-chip ${e.severity === 'hard_stop' ? 'red' : 'amber'}`}>
                      {e.severity === 'hard_stop' ? 'Hard Stop' : 'Review'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#6b7280' }}>{e.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 2c. Warnings */}
      {warnings.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title">Warnings</h4>
          <div className="warnings-list">
            {warnings.map((w, i) => (
              <div key={i} className="warning-item amber">
                <span className="icon">&#x26A0;</span>
                <span>{w}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2d. BI Requirements Cross-Reference */}
      <div className="summary-section">
        <h4 className="summary-section-title">Tender Requirements</h4>
        <div className="qualification-chips">
          <span className={`qual-chip ${
            bi.canadian_content === 'yes' ? 'red' :
            bi.canadian_content === 'no' ? 'green' : 'blue'
          }`}>
            Canadian Content: {bi.canadian_content || tender?.canadian_made || 'N/A'}
          </span>
          <span className={`qual-chip ${
            bi.experience_required ? 'amber' : 'green'
          }`}>
            Experience Required: {bi.experience_required ? 'Yes' : 'No'}
          </span>
          <span className={`qual-chip ${
            bi.indigenous_business ? 'amber' : 'green'
          }`}>
            Indigenous Business: {bi.indigenous_business ? 'Required' : 'Not Required'}
          </span>
          {bi.master_category && (
            <span className="qual-chip blue">
              Category: {bi.master_category}
            </span>
          )}
        </div>
      </div>

      {eliminations.length === 0 && warnings.length === 0 && (
        <div className="summary-section">
          <div className="recommendation-card">
            <p>No compliance issues or warnings were identified. This tender appears to have no blocking requirements for your organization.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceTab;
